import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { useForm } from "@formspree/react";
import { Send, Mail, MessageSquare, User, Globe, Github, Linkedin, MapPin } from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.3 });
  const [state, handleSubmit] = useForm("mrbjpknj"); // Replace with your Formspree form ID
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const submitPromise = handleSubmit(e);
    
    toast.promise(
      submitPromise,
      {
        loading: 'Sending message...',
        success: 'Message sent successfully! I\'ll get back to you soon.',
        error: 'Failed to send message. Please try again.',
      }
    );
    
    try {
      await submitPromise;
      if (state.succeeded) {
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  if (state.succeeded) {
    return (
      <section id="contact" className="contact section">
        <div className="container">
          <motion.div
            className="contact-success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <h2>Thank you for your message!</h2>
            <p>I'll get back to you as soon as possible.</p>
            <button 
              onClick={() => window.location.reload()}
              className="cta-button primary"
              style={{ background: 'var(--accent-color)', color: 'white', border: 'none' }}
            >
              Send Another Message
            </button>
          </motion.div>
        </div>
      </section>
    );
  }  return (
    <section id="contact" className="contact section">
      <Toaster 
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
          },
        }}
      />
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            Get In Touch
          </motion.h2>

          <motion.div className="contact-content" variants={itemVariants}>
            <motion.p className="contact-intro" variants={itemVariants}>
              I'm always interested in new opportunities, collaborations, and challenging projects.
              Whether you want to discuss a project or just connect, feel free to reach out!
            </motion.p>

            {/* Contact Information */}
            <motion.div className="contact-info-grid" variants={itemVariants}>
              <motion.div 
                className="contact-info-card"
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <Globe size={24} />
                <h3>Website</h3>
                <a href="https://rishvanth.in" target="_blank" rel="noopener noreferrer">
                  rishvanth.in
                </a>
              </motion.div>
              
              <motion.div 
                className="contact-info-card"
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <Mail size={24} />
                <h3>Email</h3>
                <a href="mailto:rish.rajaa@gmail.com">
                  rish.rajaa@gmail.com
                </a>
              </motion.div>
              
              <motion.div 
                className="contact-info-card"
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <Github size={24} />
                <h3>GitHub</h3>
                <a href="https://github.com/rishvanth03" target="_blank" rel="noopener noreferrer">
                  github.com/rishvanth03
                </a>
              </motion.div>
              
                
            </motion.div>

            <motion.form
              className="contact-form"
              onSubmit={onSubmit}
              variants={itemVariants}
            >
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    <User size={18} />
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="Your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    <Mail size={18} />
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subject" className="form-label">
                  <MessageSquare size={18} />
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                  placeholder="What's this about?"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">
                  <MessageSquare size={18} />
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-textarea"
                  required
                  placeholder="Tell me about your project or idea..."
                  rows="6"
                ></textarea>
              </div>

              <motion.button
                type="submit"
                className="submit-button"
                disabled={state.submitting}
                whileHover={{ y: -2 }}
                whileTap={{ y: 0 }}
              >
                {state.submitting ? (
                  "Sending..."
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </motion.button>
            </motion.form>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
